import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Stars } from "@/components/Stars";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const input = "w-full bg-white/5 border border-white/15 focus:border-[#FF3B30] outline-none px-3 text-sm";

export const Reviews = ({ productId }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], avg: 0, count: 0 });
  const [form, setForm] = useState({ rating: 5, title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/products/${productId}/reviews`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const mine = user && data.reviews.find((r) => r.user_id === user.id);
  useEffect(() => { if (mine) setForm({ rating: mine.rating, title: mine.title, body: mine.body }); }, [mine?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post(`/products/${productId}/reviews`, form); toast.success(mine ? "Review updated" : "Thanks for your review"); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  const remove = async () => {
    try { await api.delete(`/products/${productId}/reviews`); setForm({ rating: 5, title: "", body: "" }); toast.success("Review removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section data-testid="reviews-section" className="mt-20 border-t border-white/10 pt-12 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-12">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold">Crew feedback</p>
        <h2 className="font-display font-black uppercase tracking-tight text-3xl mt-2">Reviews</h2>
        <div className="flex items-end gap-3 mt-6">
          <span data-testid="reviews-avg" className="font-display font-black text-6xl leading-none">{data.count ? data.avg.toFixed(1) : "—"}</span>
          <div className="pb-1">
            <Stars value={data.avg} size="w-4 h-4" testid="reviews-avg-stars" />
            <p data-testid="reviews-count" className="font-mono text-xs text-neutral-500 mt-1">{data.count} review{data.count === 1 ? "" : "s"}</p>
          </div>
        </div>
        {user ? (
          <form onSubmit={submit} data-testid="review-form" className="mt-8 space-y-3 bg-[#121215] border border-white/10 p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">{mine ? "Update your review" : "Rate this gear"}</p>
            <Stars value={form.rating} size="w-6 h-6" onChange={(rating) => setForm({ ...form, rating })} testid="review-rating" />
            <input data-testid="review-title-input" className={`${input} h-10`} placeholder="Headline (optional)" maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <textarea data-testid="review-body-input" required className={`${input} h-24 py-2`} placeholder="How does it perform?" maxLength={2000} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <div className="flex gap-2">
              <button data-testid="review-submit" disabled={saving} className="flex-1 h-10 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-black tracking-wider disabled:opacity-60">{saving ? "..." : mine ? "Update" : "Post review"}</button>
              {mine && <button type="button" data-testid="review-delete" onClick={remove} className="w-10 h-10 grid place-items-center border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </form>
        ) : (
          <p className="mt-8 text-sm text-neutral-400"><Link to="/login" data-testid="review-signin-link" className="text-white underline underline-offset-4 hover:text-[#FF3B30]">Sign in</Link> to leave a review.</p>
        )}
      </div>
      <div className="space-y-4">
        {data.reviews.length === 0 && <p data-testid="reviews-empty" className="text-neutral-500 text-sm">No reviews yet. Be the first to rate it.</p>}
        {data.reviews.map((r) => (
          <article key={r.id} data-testid={`review-${r.id}`} className="border border-white/10 bg-[#121215] p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <Stars value={r.rating} />
                <span className="font-display uppercase font-bold text-sm">{r.user_name}</span>
                {r.verified && <span data-testid="review-verified" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-emerald-400"><BadgeCheck className="w-3.5 h-3.5" />Verified buyer</span>}
              </div>
              <span className="font-mono text-[11px] text-neutral-500">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
            {r.title && <p className="font-semibold mt-3">{r.title}</p>}
            <p className="text-sm text-neutral-300 mt-1 whitespace-pre-line">{r.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
};
